import React, { useState, useEffect, useCallback } from 'react';
import './DashboardKPI.css';
import baseURL from '../../url';
import moneda from '../../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faReceipt,
    faChartLine,
    faClock,
    faFire,
    faCheckDouble,
    faCashRegister,
    faSync,
    faPlus,
    faArrowUp,
    faArrowDown,
    faUtensils,
    faHandHoldingDollar
} from '@fortawesome/free-solid-svg-icons';

export default function DashboardKPI({ onAbrirModalGasto }) {
    const [filtro, setFiltro] = useState('mes');
    const [desde, setDesde] = useState('');
    const [hasta, setHasta] = useState('');
    const [loading, setLoading] = useState(true);
    const [kpi, setKpi] = useState({
        ventasHoy: 0,
        totalVentas: 0,
        ingresosCobrados: 0,
        totalGastos: 0,
        gananciaEstimada: 0,
        cantPedidos: 0,
        pedidosPendientes: 0,
        pedidosEnCocina: 0,
        pedidosListos: 0,
        pedidosEntregados: 0,
        pedidosPagados: 0,
        pedidosPendientesCobro: 0,
        montoPendienteCobro: 0,
        variacionVentas: 0
    });

    const cargarKPIs = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${baseURL}/dashboardKpiGet.php?filtro=${filtro}`;
            if (filtro === 'personalizado' && desde && hasta) {
                url += `&desde=${desde}&hasta=${hasta}`;
            }
            const res = await fetch(url);
            const data = await res.json();
            if (data && data.kpi) {
                setKpi(data.kpi);
            }
        } catch (e) {
            console.error('Error al cargar KPIs:', e);
        } finally {
            setLoading(false);
        }
    }, [filtro, desde, hasta]);

    useEffect(() => {
        cargarKPIs();
    }, [cargarKPIs]);

    const handleFiltroRapido = (tipo) => {
        setFiltro(tipo);
    };

    return (
        <div className="dashboardKpiContainer">
            {/* Barra de Filtros de Período */}
            <div className="kpiFilterBar">
                <div className="kpiFilterPills">
                    <button
                        className={`filterPill ${filtro === 'hoy' ? 'active' : ''}`}
                        onClick={() => handleFiltroRapido('hoy')}
                    >
                        Hoy
                    </button>
                    <button
                        className={`filterPill ${filtro === 'ayer' ? 'active' : ''}`}
                        onClick={() => handleFiltroRapido('ayer')}
                    >
                        Ayer
                    </button>
                    <button
                        className={`filterPill ${filtro === 'semana' ? 'active' : ''}`}
                        onClick={() => handleFiltroRapido('semana')}
                    >
                        Esta semana
                    </button>
                    <button
                        className={`filterPill ${filtro === 'mes' ? 'active' : ''}`}
                        onClick={() => handleFiltroRapido('mes')}
                    >
                        Este mes
                    </button>
                    <button
                        className={`filterPill ${filtro === 'anio' ? 'active' : ''}`}
                        onClick={() => handleFiltroRapido('anio')}
                    >
                        Este año
                    </button>
                    <button
                        className={`filterPill ${filtro === 'personalizado' ? 'active' : ''}`}
                        onClick={() => setFiltro('personalizado')}
                    >
                        Personalizado
                    </button>
                </div>

                {filtro === 'personalizado' && (
                    <div className="kpiCustomDates">
                        <input
                            type="date"
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                            className="kpiDateInput"
                        />
                        <span>a</span>
                        <input
                            type="date"
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                            className="kpiDateInput"
                        />
                        <button className="btnAplicarFechas" onClick={cargarKPIs}>
                            Filtrar
                        </button>
                    </div>
                )}

                <div className="kpiActionsRight">
                    {onAbrirModalGasto && (
                        <button className="btnRegistrarGastoTop" onClick={onAbrirModalGasto}>
                            <FontAwesomeIcon icon={faPlus} /> Registrar Gasto
                        </button>
                    )}
                    <button className="btnReloadKpi" onClick={cargarKPIs} title="Actualizar datos">
                        <FontAwesomeIcon icon={faSync} className={loading ? 'fa-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* SECCIÓN 1: Tarjetas Financieras */}
            <div className="kpiCardsGrid financialGrid">
                {/* Ventas Totales */}
                <div className="kpiCard cardVentas">
                    <div className="kpiCardTop">
                        <span className="kpiCardTitle">Ventas del Período</span>
                        <div className="kpiCardIconWrapper iconVentas">
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                        </div>
                    </div>
                    <div className="kpiCardValue">
                        {moneda} {Number(kpi.totalVentas || 0).toFixed(2)}
                    </div>
                    <div className="kpiCardSub">
                        <span>Hoy: <strong>{moneda} {Number(kpi.ventasHoy || 0).toFixed(2)}</strong></span>
                        {kpi.variacionVentas !== 0 && (
                            <span className={`kpiTrend ${kpi.variacionVentas > 0 ? 'trendUp' : 'trendDown'}`}>
                                <FontAwesomeIcon icon={kpi.variacionVentas > 0 ? faArrowUp : faArrowDown} /> {Math.abs(kpi.variacionVentas)}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Ingresos Cobrados */}
                <div className="kpiCard cardIngresos">
                    <div className="kpiCardTop">
                        <span className="kpiCardTitle">Ingresos Cobrados</span>
                        <div className="kpiCardIconWrapper iconIngresos">
                            <FontAwesomeIcon icon={faHandHoldingDollar} />
                        </div>
                    </div>
                    <div className="kpiCardValue">
                        {moneda} {Number(kpi.ingresosCobrados || 0).toFixed(2)}
                    </div>
                    <div className="kpiCardSub">
                        <span>Cobros confirmados</span>
                    </div>
                </div>

                {/* Gastos Registrados */}
                <div className="kpiCard cardGastos">
                    <div className="kpiCardTop">
                        <span className="kpiCardTitle">Gastos Registrados</span>
                        <div className="kpiCardIconWrapper iconGastos">
                            <FontAwesomeIcon icon={faReceipt} />
                        </div>
                    </div>
                    <div className="kpiCardValue">
                        {moneda} {Number(kpi.totalGastos || 0).toFixed(2)}
                    </div>
                    <div className="kpiCardSub">
                        <span>Costos operativos</span>
                    </div>
                </div>

                {/* Ganancia Estimada */}
                <div className="kpiCard cardGanancia">
                    <div className="kpiCardTop">
                        <span className="kpiCardTitle">Ganancia Estimada</span>
                        <div className="kpiCardIconWrapper iconGanancia">
                            <FontAwesomeIcon icon={faChartLine} />
                        </div>
                    </div>
                    <div className={`kpiCardValue ${kpi.gananciaEstimada >= 0 ? 'gananciaPositiva' : 'gananciaNegativa'}`}>
                        {moneda} {Number(kpi.gananciaEstimada || 0).toFixed(2)}
                    </div>
                    <div className="kpiCardSub">
                        <span>Ingresos − Gastos</span>
                    </div>
                </div>
            </div>

            {/* SECCIÓN 2: Métricas Operativas */}
            <div className="kpiCardsGrid operationalGrid">
                {/* Total Pedidos */}
                <div className="kpiMiniCard">
                    <div className="miniCardLeft">
                        <span className="miniCardLabel">Total Pedidos</span>
                        <strong className="miniCardVal">{kpi.cantPedidos}</strong>
                    </div>
                    <div className="miniCardIcon bg-blue">
                        <FontAwesomeIcon icon={faUtensils} />
                    </div>
                </div>

                {/* Pendientes */}
                <div className="kpiMiniCard">
                    <div className="miniCardLeft">
                        <span className="miniCardLabel">Pendientes</span>
                        <strong className="miniCardVal text-yellow">{kpi.pedidosPendientes}</strong>
                    </div>
                    <div className="miniCardIcon bg-yellow">
                        <FontAwesomeIcon icon={faClock} />
                    </div>
                </div>

                {/* En Cocina */}
                <div className="kpiMiniCard">
                    <div className="miniCardLeft">
                        <span className="miniCardLabel">En Cocina</span>
                        <strong className="miniCardVal text-purple">{kpi.pedidosEnCocina}</strong>
                    </div>
                    <div className="miniCardIcon bg-purple">
                        <FontAwesomeIcon icon={faFire} />
                    </div>
                </div>

                {/* Listos / Entregados */}
                <div className="kpiMiniCard">
                    <div className="miniCardLeft">
                        <span className="miniCardLabel">Listos / Entregados</span>
                        <strong className="miniCardVal text-green">{kpi.pedidosListos + kpi.pedidosEntregados}</strong>
                    </div>
                    <div className="miniCardIcon bg-green">
                        <FontAwesomeIcon icon={faCheckDouble} />
                    </div>
                </div>

                {/* Pendientes de Cobro */}
                <div className="kpiMiniCard cardCobroPendiente">
                    <div className="miniCardLeft">
                        <span className="miniCardLabel">Por Cobrar</span>
                        <strong className="miniCardVal text-orange">{kpi.pedidosPendientesCobro}</strong>
                        <small className="miniCardMonto">({moneda} {Number(kpi.montoPendienteCobro || 0).toFixed(2)})</small>
                    </div>
                    <div className="miniCardIcon bg-orange">
                        <FontAwesomeIcon icon={faCashRegister} />
                    </div>
                </div>
            </div>
        </div>
    );
}
